import React from 'react';

import { FormattedMessage } from 'react-intl';

import LinkExternalIcon from '@atlaskit/icon/core/link-external';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Anchor, Box, Flex, Inline, xcss } from '@atlaskit/primitives';
import { FeedbackButton } from '@atlassian/search-common/feedback-button';

import { PrimaryProductKeys } from '../../../../common/constants/primary-product-keys';
import FeedbackCollector from '../../../../common/ui/feedback-collector';
import {
	useApplicationMode,
	useCloudId,
	useExperimentConfig,
	usePrimaryProduct,
	useUser,
} from '../../../../controllers/store/bootstrap';
import {
	useFetchEntitiesRequestId,
	useFetchEntitiesTraceId,
	useIsFeedbackModalOpen,
} from '../../../../controllers/store/quick-find';
import { useSelectedProducts } from '../../../../controllers/store/search';

import { messages } from './messages';

const footerContainerStyles = xcss({
	paddingBlockStart: 'space.150',
	paddingBlockEnd: 'space.150',
	paddingInlineStart: 'space.200',
	paddingInlineEnd: 'space.200',
	color: 'color.text',
});

const communityLinkStyles = xcss({
	font: 'font.body.small',
	color: 'color.text.subtlest',
	textDecoration: 'none',
	':hover': {
		textDecoration: 'underline',
		color: 'color.text.subtlest',
	},
	':visited': {
		color: 'color.text.subtlest',
	},
});

const footerTextStyles = xcss({
	font: 'font.body.small',
	color: 'color.text.subtlest',
});

export const FeedbackFooter = (): React.JSX.Element => {
	const [feedbackIsOpen, { setIsFeedbackModalOpen }] = useIsFeedbackModalOpen();

	const [cloudId] = useCloudId();
	const [experimentConfig] = useExperimentConfig();
	const [primaryProduct] = usePrimaryProduct();
	const [selectedProducts] = useSelectedProducts();
	const [user] = useUser();
	const applicationMode = useApplicationMode();
	const requestId = useFetchEntitiesRequestId();
	const traceId = useFetchEntitiesTraceId();

	return (
		<Flex justifyContent="space-between" xcss={footerContainerStyles}>
			<Inline alignBlock="center" space="space.150">
				<Box xcss={footerTextStyles}>
					<FormattedMessage {...messages.feedbackFooter} />
				</Box>
				<FeedbackButton
					size="small"
					onClick={(e) => {
						e.preventDefault();
						setIsFeedbackModalOpen(true);
					}}
				/>
				<FeedbackCollector
					cloudId={cloudId || ''}
					experimentConfig={experimentConfig}
					isOpen={feedbackIsOpen}
					primaryProduct={primaryProduct || ''}
					product={selectedProducts.length ? selectedProducts.join(', ') : 'no product filter'}
					setIsOpen={setIsFeedbackModalOpen}
					source="quick-find"
					userId={user.id || ''}
					userName={user.name || ''}
					mode={applicationMode}
					requestId={requestId || ''}
					traceId={traceId || ''}
				/>
			</Inline>
			{primaryProduct === PrimaryProductKeys.Jira && (
				<Anchor
					xcss={communityLinkStyles}
					href="https://community.atlassian.com/forums/Jira-articles/Introducing-Rovo-Search-for-Jira/ba-p/3093745"
					target="_blank"
				>
					<Inline alignBlock="center" space="space.075">
						<FormattedMessage {...messages.searchChangeMessage} />
						<LinkExternalIcon size="small" label="Link to community post" />
					</Inline>
				</Anchor>
			)}
		</Flex>
	);
};
